/**
 * Colour swatches Web Component for PDP.
 * Fetches sibling products from a master-collection and renders them as swatches.
 *
 * Mirrors ColourSwatches from the old Expanse 4 theme.js (line 11238).
 * Settings are read from <script type="application/json" id="swatches-settings">.
 *
 * Tag convention: "master-collection:bracelet-a2221"
 * Collection handle: replace first ":" with "-" → "master-collection-bracelet-a2221"
 */
class ColourSwatches extends HTMLElement {
  constructor() {
    super();
    this.container = this.querySelector('.colour-swatches__container');
    this.settings = this._readSettings();
    this.wrapper = this.closest('.product-block--color-swatches') || this;

    const { groupTag } = this.settings;
    if (!groupTag) {
      this._hideWrapper();
      return;
    }

    const collection = groupTag.replace(':', '-');
    fetch(`/collections/${encodeURIComponent(collection)}?view=data`)
      .then((r) => {
        // A non-existent collection handle resolves to Shopify's normal 404
        // HTML page rather than our JSON template, so guard before parsing.
        if (!r.ok || !r.headers.get('content-type')?.includes('json')) {
          return null;
        }
        return r.json();
      })
      .then((products) => {
        if (!products || products.length === 0) {
          this._hideWrapper();
          return;
        }
        this._initSwatches(products);
        this._setCurrent();
      })
      .catch((err) => {
        console.error('[ColourSwatches] fetch error:', err);
        this._hideWrapper();
      });
  }

  _readSettings() {
    const el = this.querySelector('#swatches-settings');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch {
      return {};
    }
  }

  _hideWrapper() {
    this.wrapper.classList.add('swatch-empty--hide');
  }

  _getImageSrc(product) {
    switch (this.settings.image_type) {
      case 'first_variant_image': {
        const img = product.variants?.[0]?.image;
        if (img) return img;
        break;
      }
      case 'last_variant_image': {
        const img = product.variants?.[product.variants.length - 1]?.image;
        if (img) return img;
        break;
      }
      case 'image_swatch': {
        const colourTag = (product.tags || []).find(
          (t) => t.indexOf(this.settings.colourTagPrefix) > -1
        );
        if (colourTag && this.settings.swatchUrl) {
          const colour = colourTag.replace(this.settings.colourTagPrefix, '').toLowerCase().trim();
          return 'https:' + this.settings.swatchUrl.replace('swatch', `colour-${colour}`);
        }
        break;
      }
    }
    return product.featured_image;
  }

  _initSwatches(products) {
    let html = '';
    products.forEach((product) => {
      const imgSrc = this._getImageSrc(product);
      html += `<input swatch-input type="image"
        data-available="${product.available}"
        data-handle="${product.handle}"
        src="${imgSrc}"
        alt="${product.title}"
        data-href="/products/${product.handle}">`;
    });

    if (html) {
      this.container.innerHTML = html;
      const inputs = this.container.querySelectorAll('[swatch-input]');
      inputs.forEach((input) => {
        input.addEventListener('click', (e) => {
          e.preventDefault();
          window.location.href = e.currentTarget.dataset.href;
        });
      });
    }
  }

  _setCurrent() {
    const currentHandle = window.Shopify?.designMode
      ? null
      : document.querySelector('[data-product-handle]')?.dataset.productHandle;

    const handle = currentHandle || location.pathname.split('/products/')[1]?.split('?')[0];
    if (!handle) return;

    const current = this.container.querySelector(`[data-handle="${handle}"]`);
    if (current) current.classList.add('selected');
  }
}

customElements.define('colour-swatches', ColourSwatches);
